export const AUDIO_LOAD_ERROR = 'Audio load failed'
export const AUDIO_PLAY_ERROR = 'Audio playback blocked'

export function howlerPlay(
  sound: Howl,
  progressCallback: (progress: number, duration: number) => void,
  startCallback?: () => void,
  endCallback?: () => void,
  signal?: AbortSignal,
) {
  return new Promise<void>((resolve, reject) => {
    let frame: number | undefined
    let settled = false
    const timeout = window.setTimeout(() => {
      fail(AUDIO_LOAD_ERROR, 'Audio did not start within 30000 ms')
    }, 30000)

    function cleanUp() {
      window.clearTimeout(timeout)
      if (frame !== undefined) cancelAnimationFrame(frame)
      sound.off('loaderror', onLoadError)
      sound.off('playerror', onPlayError)
      sound.off('play', onPlay)
      sound.off('end', onEnd)
      sound.off('stop', onStop)
      signal?.removeEventListener('abort', onStop)
    }

    function fail(message: string, cause?: unknown) {
      if (settled) return
      settled = true
      cleanUp()
      progressCallback(0, sound.duration())
      reject(Object.assign(new Error(message), { cause }))
    }

    function tick() {
      if (sound.playing()) {
        const duration = sound.duration()
        progressCallback(duration > 0 ? sound.seek() / duration : 0, duration)
      }
      frame = requestAnimationFrame(tick)
    }

    function onLoadError(soundId: number, error: unknown) {
      fail(AUDIO_LOAD_ERROR, {
        event: 'loaderror',
        soundId,
        error,
        state: sound.state(),
      })
    }
    function onPlayError(soundId: number, error: unknown) {
      fail(AUDIO_PLAY_ERROR, {
        event: 'playerror',
        soundId,
        error,
        state: sound.state(),
      })
    }
    function onPlay() {
      window.clearTimeout(timeout)
      startCallback?.()
      tick()
    }
    function finish() {
      if (settled) return
      settled = true
      cleanUp()
      endCallback?.()
      resolve()
    }
    function onEnd() {
      progressCallback(1, sound.duration())
      finish()
    }
    function onStop() {
      finish()
    }

    if (signal?.aborted) {
      finish()
      return
    }
    signal?.addEventListener('abort', onStop, { once: true })
    sound.once('loaderror', onLoadError)
    sound.once('playerror', onPlayError)
    sound.once('play', onPlay)
    sound.once('end', onEnd)
    sound.once('stop', onStop)
    progressCallback(0, sound.duration())
    if (sound.state() === 'unloaded') sound.load()
    // load() may synchronously emit loaderror and settle the promise.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!settled) sound.play()
  })
}
